import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
    CheckCircleIcon, 
    ExclamationCircleIcon, 
    InformationCircleIcon,
    XMarkIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const addToast = useCallback(({ type = 'info', title, message, duration = 5000, action }) => {
        const id = Date.now() + Math.random();
        const newToast = { id, type, title, message, duration, action };
        
        setToasts(prev => [...prev, newToast]);

        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }

        return id;
    }, [removeToast]);

    const success = useCallback((title, message, options = {}) => {
        return addToast({ type: 'success', title, message, ...options });
    }, [addToast]);

    const error = useCallback((title, message, options = {}) => {
        return addToast({ type: 'error', title, message, ...options });
    }, [addToast]);

    const info = useCallback((title, message, options = {}) => {
        return addToast({ type: 'info', title, message, ...options });
    }, [addToast]);

    const warning = useCallback((title, message, options = {}) => {
        return addToast({ type: 'warning', title, message, ...options });
    }, [addToast]);

    const getToastStyles = (type) => {
        switch (type) {
            case 'success':
                return {
                    bg: 'bg-green-50 border-green-200',
                    icon: CheckCircleIcon,
                    iconColor: 'text-green-500',
                    titleColor: 'text-green-800',
                    messageColor: 'text-green-700'
                };
            case 'error':
                return {
                    bg: 'bg-red-50 border-red-200',
                    icon: ExclamationCircleIcon,
                    iconColor: 'text-red-500',
                    titleColor: 'text-red-800',
                    messageColor: 'text-red-700'
                };
            case 'warning':
                return {
                    bg: 'bg-yellow-50 border-yellow-200',
                    icon: ExclamationTriangleIcon,
                    iconColor: 'text-yellow-500',
                    titleColor: 'text-yellow-800',
                    messageColor: 'text-yellow-700'
                };
            default:
                return {
                    bg: 'bg-blue-50 border-blue-200',
                    icon: InformationCircleIcon,
                    iconColor: 'text-blue-500',
                    titleColor: 'text-blue-800',
                    messageColor: 'text-blue-700'
                };
        }
    };

    return (
        <ToastContext.Provider value={{ success, error, info, warning, addToast, removeToast }}>
            {children}
            
            {/* Toast Container */}
            <div className="fixed top-20 right-4 z-50 space-y-3 max-w-sm">
                {toasts.map((toast) => {
                    const styles = getToastStyles(toast.type);
                    const Icon = styles.icon;
                    
                    return (
                        <div
                            key={toast.id}
                            className={`${styles.bg} border rounded-xl shadow-lg p-4 transform transition-all duration-300 ease-out animate-slide-in-right`}
                            style={{
                                animation: 'slideInRight 0.3s ease-out'
                            }}
                        >
                            <div className="flex items-start">
                                <Icon className={`w-5 h-5 ${styles.iconColor} mt-0.5 flex-shrink-0`} />
                                <div className="ml-3 flex-1 min-w-0">
                                    {toast.title && (
                                        <p className={`text-sm font-semibold ${styles.titleColor} mb-1`}>
                                            {toast.title}
                                        </p>
                                    )}
                                    {toast.message && (
                                        <p className={`text-sm ${styles.messageColor}`}>
                                            {toast.message}
                                        </p>
                                    )}
                                    {toast.action && (
                                        <button
                                            onClick={() => {
                                                toast.action.onClick();
                                                removeToast(toast.id);
                                            }}
                                            className="mt-2 text-sm font-medium underline hover:no-underline"
                                        >
                                            {toast.action.label}
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={() => removeToast(toast.id)}
                                    className="ml-3 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            </div>
                            
                            {/* Progress bar */}
                            {toast.duration > 0 && (
                                <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-current opacity-30 transition-all duration-300 ease-linear"
                                        style={{
                                            width: '100%',
                                            animation: `shrink ${toast.duration}ms linear forwards`
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
};

export default ToastProvider;